import { useCallback, useState } from 'react'
import { useAtomValue } from 'jotai'
import { feelgreatSettingsAtom, fgProductsAtom } from '@/core/feelgreat/feelgreat.store'
import { usePriceMode, getProductPrice } from './useCart'
import type { CartItems } from './cart.store'
import type { FGLocalText } from '@/core/feelgreat/feelgreat.types'

interface UseCartLimitOptions {
    /** Currency label displayed in the "exceeds X" error message. */
    currency?: string
}

const parseLocalTextNumber = (val: unknown): number => {
    const text = (val as FGLocalText | undefined)?.english
    if (!text) return NaN
    return Number(text)
}

/**
 * Reads `cart_limit_qty` and `cart_limit_price` from
 * `feelgreatSettingsAtom` and exposes a `canIncrease` predicate plus the
 * resulting error message.
 *
 * Wire `canIncrease` into `useCartMutations` so that increases are
 * validated client-side before any server round-trip. Decreases / removes
 * are intentionally not gated (matches legacy behaviour: a cart that
 * already exceeds the limit should still be shrinkable).
 */
export const useCartLimit = (options: UseCartLimitOptions = {}) => {
    const { currency = '' } = options
    const settings = useAtomValue(feelgreatSettingsAtom)
    const fgProducts = useAtomValue(fgProductsAtom)
    const priceMode = usePriceMode()
    const [error, setError] = useState<string | null>(null)

    const canIncrease = useCallback(
        (newCart: CartItems): boolean => {
            if (!settings) return true

            const maxQty = parseLocalTextNumber((settings as Record<string, unknown>).cart_limit_qty)
            const maxPrice = parseLocalTextNumber((settings as Record<string, unknown>).cart_limit_price)

            if (!isNaN(maxQty) && maxQty > 0) {
                for (const flavorMap of Object.values(newCart)) {
                    for (const qty of Object.values(flavorMap)) {
                        if (qty > maxQty) {
                            setError(`Maximum ${maxQty} items per product`)
                            return false
                        }
                    }
                }
            }

            if (!isNaN(maxPrice) && maxPrice > 0) {
                let totalPrice = 0
                for (const [purchaseType, flavorMap] of Object.entries(newCart)) {
                    for (const [flavor, qty] of Object.entries(flavorMap)) {
                        totalPrice += getProductPrice(fgProducts, flavor, purchaseType, priceMode) * qty
                    }
                }
                if (totalPrice > maxPrice) {
                    setError(`Cart total exceeds ${maxPrice}${currency ? ` ${currency}` : ''}`)
                    return false
                }
            }

            setError(null)
            return true
        },
        [settings, fgProducts, priceMode, currency]
    )

    const clearError = useCallback(() => setError(null), [])

    return { canIncrease, error, clearError }
}
